import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Clock, TrendUp, Trophy, Coins, Car, House, ShieldWarning, Warning } from "@phosphor-icons/react";

import { Progress } from "../components/ui/progress";
import HistoryChart from "../components/HistoryChart";

const fmt = (n) => new Intl.NumberFormat("de-DE").format(n || 0);
const fmtHours = (m) => `${Math.floor((m || 0) / 60)}h`;

export default function Stats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/character/stats").then(r => setStats(r.data)).catch(() => toast.error("Fehler"));
  }, []);

  if (!stats) return <div className="p-12 text-white/40 font-mono text-sm">Lade Statistiken...</div>;
  const xpPct = stats.xp_to_next ? Math.round((stats.xp / stats.xp_to_next) * 100) : 0;

  const cards = [
    { icon: Clock, label: "Playtime", value: fmtHours(stats.playtime_minutes), sub: `${fmt(stats.playtime_minutes)} Minuten insgesamt`, tone: "volt" },
    { icon: Trophy, label: "Level", value: stats.level, sub: `${xpPct}% zum nächsten`, tone: "warn" },
    { icon: TrendUp, label: "XP", value: fmt(stats.xp), sub: `von ${fmt(stats.xp_to_next)} benötigt`, tone: "default" },
    { icon: Coins, label: "Vermögen", value: `$${fmt(stats.total_wealth)}`, sub: "Cash + Bank", tone: "success" },
    { icon: Car, label: "Fahrzeuge", value: stats.vehicles_count, sub: "im Fuhrpark", tone: "default" },
    { icon: House, label: "Immobilien", value: stats.properties_count, sub: "im Besitz", tone: "default" },
    { icon: ShieldWarning, label: "Strafakte", value: stats.record_count, sub: "Einträge", tone: stats.record_count ? "danger" : "success" },
    { icon: Warning, label: "Warnungen", value: stats.warning_count, sub: "von Admins", tone: stats.warning_count ? "warn" : "success" },
  ];

  const toneCls = { volt: "text-volt", success: "text-tactical-success", danger: "text-tactical-danger", warn: "text-tactical-warn", default: "text-white" };

  return (
    <div>
      <PageHeader
        eyebrow="06 // STATISTICS"
        title="Statistiken"
        description="Alle Zahlen deines Chars auf einen Blick."
      />
      <div className="p-4 md:p-8 lg:p-12 space-y-6 md:space-y-8">
        <div className="border border-tactical-border bg-tactical-surface p-6">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Level Fortschritt</div>
              <div className="font-display font-bold text-3xl mt-1">Level {stats.level}</div>
            </div>
            <div className="font-mono text-right">
              <div className="text-tactical-success text-sm">+{xpPct}%</div>
              <div className="text-white/40 text-xs">{fmt(stats.xp)} / {fmt(stats.xp_to_next)} XP</div>
            </div>
          </div>
          <Progress value={xpPct} className="h-2 bg-tactical-border" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} data-testid={`stat-card-${c.label.toLowerCase()}`} className="border border-tactical-border bg-tactical-surface p-5 hover:border-white/30 transition-colors duration-150">
                <div className="flex items-center justify-between mb-4">
                  <Icon size={22} className={toneCls[c.tone]} weight="duotone" />
                  <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{c.label}</div>
                </div>
                <div className={`font-mono font-bold text-3xl ${toneCls[c.tone]}`}>{c.value}</div>
                <div className="font-mono text-[10px] text-white/40 mt-1">{c.sub}</div>
              </div>
            );
          })}
        </div>

        <HistoryChart days={30} />
      </div>
    </div>
  );
}
